import React from 'react';
import { DnsRecord } from '../../types/domain';
import { Button } from '../ui/button';
import { Copy, Check } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

interface DnsRecordsDisplayProps {
  records: DnsRecord[];
}

export const DnsRecordsDisplay: React.FC<DnsRecordsDisplayProps> = ({ records }) => {
  const { toast } = useToast();
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast({
        title: 'Copied!',
        description: 'Value copied to clipboard',
      });
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  if (records.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        <p className="font-medium mb-2">Configure these DNS records with your domain provider:</p>
        <ol className="list-decimal list-inside space-y-1 text-xs">
          <li>Log in to your domain registrar (GoDaddy, Namecheap, etc.)</li>
          <li>Navigate to DNS settings for your domain</li>
          <li>Add the CNAME record(s) below</li>
          <li>Wait 5-10 minutes for DNS propagation</li>
          <li>Click "Verify Domain" to check the configuration</li>
        </ol>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Type</th>
              <th className="px-4 py-3 text-left font-medium">Name</th>
              <th className="px-4 py-3 text-left font-medium">Value</th>
              <th className="px-4 py-3 text-left font-medium">TTL</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {records.map((record, index) => (
              <tr key={index} className="hover:bg-muted/50">
                <td className="px-4 py-3">
                  <span className="font-mono text-xs bg-secondary px-2 py-1 rounded">
                    {record.type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-muted px-2 py-1 rounded break-all">
                      {record.key}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleCopy(record.key, `key-${index}`)}
                    >
                      {copiedField === `key-${index}` ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-muted px-2 py-1 rounded break-all">
                      {record.value}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleCopy(record.value, `value-${index}`)}
                    >
                      {copiedField === `value-${index}` ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs text-muted-foreground">
                    {record.ttl || 3600}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {record.status === 'success' && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600">
                      <Check className="h-3 w-3" />
                      Verified
                    </span>
                  )}
                  {record.status === 'pending' && (
                    <span className="text-xs text-yellow-600">Pending</span>
                  )}
                  {record.status === 'error' && (
                    <span className="text-xs text-red-600">Error</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-muted-foreground bg-muted p-3 rounded">
        <p className="font-medium mb-1">💡 Tip:</p>
        <p>
          DNS changes can take up to 48 hours to fully propagate, but typically complete
          within 5-10 minutes. You can use tools like{' '}
          <a
            href="https://dnschecker.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            dnschecker.org
          </a>{' '}
          to verify your DNS records are propagating correctly.
        </p>
      </div>
    </div>
  );
};
